import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Loader, AlertCircle, Droplets } from 'lucide-react';
import { subscriptionService } from '../services/subscriptionService';
import { useBusinessContext } from '../contexts/BusinessContext';
import PlanSelectionStep from '../components/checkout/PlanSelectionStep';
import PaymentDetailsStep from '../components/checkout/PaymentDetailsStep';
import CheckoutProgress from '../components/checkout/CheckoutProgress';

const PremiumCheckoutPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useBusinessContext();
  
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  
  // Selected plan state
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [billingInterval, setBillingInterval] = useState('monthly');
  
  const preselectedPlan = searchParams.get('plan');

  useEffect(() => {
    loadPlans();
  }, []);

  useEffect(() => {
    // Auto-select plan if specified in URL
    if (preselectedPlan && plans.length > 0) {
      const plan = plans.find(p => p.name.toLowerCase() === preselectedPlan.toLowerCase());
      if (plan) {
        setSelectedPlan(plan);
        // If plan is preselected, could optionally skip to step 2
        // setCurrentStep(2); 
      }
    }
  }, [preselectedPlan, plans]);

  const loadPlans = async () => {
    try {
      setLoading(true);
      const plansData = await subscriptionService.getPlans();
      setPlans(plansData);
    } catch (err) {
      setError('Failed to load subscription plans. Please try again later.');
      console.error('Error loading plans:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePlanSelect = (plan) => {
    setSelectedPlan(plan);
    setCurrentStep(2);
    // Scroll to top smoothly
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBackToPlans = () => {
    setCurrentStep(1);
    setSelectedPlan(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePaymentSuccess = () => {
    navigate('/subscription-success');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <Loader className="h-12 w-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading subscription plans...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50">
      
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-10 h-10 bg-blue-600 rounded-lg">
                <Droplets className="text-white" size={24} />
              </div>
              <div>
                <h1 className="font-bold text-lg">SuddyFlow</h1>
                <p className="text-xs text-gray-500">Veteran-Owned</p>
              </div>
            </div>
            
            {/* Progress indicator - only show when past step 1 */}
            {currentStep > 1 && (
              <CheckoutProgress currentStep={currentStep} />
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-8 md:py-12">
        
        {/* Error Message */}
        {error && (
          <div className="mb-8 bg-red-50 border-l-4 border-red-500 rounded-lg p-4 max-w-2xl mx-auto animate-slide-up">
            <div className="flex items-start">
              <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
              <p className="text-red-800">{error}</p>
            </div>
          </div>
        )}

        {/* Step 1: Plan Selection */}
        {currentStep === 1 && (
          <PlanSelectionStep
            plans={plans}
            selectedPlan={selectedPlan}
            billingInterval={billingInterval}
            onBillingIntervalChange={setBillingInterval}
            onPlanSelect={handlePlanSelect}
            preselectedPlan={preselectedPlan}
          />
        )}

        {/* Step 2: Payment Details */}
        {currentStep === 2 && selectedPlan && (
          <PaymentDetailsStep
            plan={selectedPlan}
            billingInterval={billingInterval}
            onBack={handleBackToPlans}
            onSuccess={handlePaymentSuccess}
          />
        )}
      </div>

      {/* Footer Trust Signals */}
      <div className="border-t border-gray-200 bg-white py-6 mt-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-gray-600">
            <div className="flex items-center gap-2">
              <span className="text-green-600">✓</span>
              <span>14-Day Free Trial</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-green-600">✓</span>
              <span>Cancel Anytime</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-green-600">✓</span>
              <span>Secure Payment by Stripe</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-green-600">✓</span>
              <span>PCI Compliant</span>
            </div>
          </div>
          <div className="text-center mt-4 text-xs text-gray-500">
            Questions? Email{' '}
            <a href="mailto:support@suddyflow.com" className="text-blue-600 hover:text-blue-700">
              support@suddyflow.com
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PremiumCheckoutPage;